import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SecondaryButton from '../atoms/SecondaryButton'
import TablePagination from './TablePagination'
import DocumentListRow from './DocumentListRow'
import DocumentFileSvg from '../other/svg/DocumentFileSvg'
import PlusSvg from '../other/svg/PlusSvg'
import SearchSvg from '../other/svg/SearchSvg'
import { Document } from 'src/redux/documentsReducer'
import { VehicleDocumentType, formatDocumentDate } from 'src/helpers/addVehicleDocument'

interface ClientDocumentsPanelProps {
  documents: Document[]
  currentPage: number
  totalPages: number
  onSelectDocumentType: (documentType: VehicleDocumentType) => void
  onOpenDocument: (document: Document) => void
  onDownloadDocument: (document: Document) => void
  onEditDocument: (document: Document) => void
  onDeleteDocument: (document: Document) => void
  onOpenVehicle: (document: Document) => void
  onPageChange: (page: number) => void
}

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const searchShadow = '0px 0px 0px 1px rgba(0,0,0,0.08), 0px 1px 2px 0px rgba(0,0,0,0.12)'

const documentTypeOptions: Array<{
  type: VehicleDocumentType
  labelKey: 'my_vehicles.document_invoice' | 'my_vehicles.document_sell_contract' | 'my_vehicles.document_import'
}> = [
  { type: 'invoice', labelKey: 'my_vehicles.document_invoice' },
  { type: 'sell_contract', labelKey: 'my_vehicles.document_sell_contract' },
  { type: 'import_document', labelKey: 'my_vehicles.document_import' }
]

const listTypeLabelKey = (
  documentType: string
): 'clients.document_invoice' | 'clients.document_contract' | 'clients.document_import' | null => {
  if (documentType === 'invoice') {
    return 'clients.document_invoice'
  }
  if (documentType === 'sell_contract') {
    return 'clients.document_contract'
  }
  if (documentType === 'import_document') {
    return 'clients.document_import'
  }
  return null
}

const ClientDocumentsPanel = ({
  documents,
  currentPage,
  totalPages,
  onSelectDocumentType,
  onOpenDocument,
  onDownloadDocument,
  onEditDocument,
  onDeleteDocument,
  onOpenVehicle,
  onPageChange
}: ClientDocumentsPanelProps): JSX.Element => {
  const { t } = useTranslation()
  const isEmpty = documents.length === 0
  const [menuAnchor, setMenuAnchor] = useState<'header' | 'empty' | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [openRowKey, setOpenRowKey] = useState<string | null>(null)
  const headerMenuRef = useRef<HTMLDivElement>(null)
  const emptyMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      const headerContains = headerMenuRef.current?.contains(event.target as Node) === true
      const emptyContains = emptyMenuRef.current?.contains(event.target as Node) === true
      if (!headerContains && !emptyContains) {
        setMenuAnchor(null)
      }
    }

    if (menuAnchor !== null) {
      document.addEventListener('mousedown', handleClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [menuAnchor])

  const handleSelectType = (documentType: VehicleDocumentType): void => {
    setMenuAnchor(null)
    onSelectDocumentType(documentType)
  }

  const toggleMenu = (anchor: 'header' | 'empty'): void => {
    setMenuAnchor(menuAnchor === anchor ? null : anchor)
  }

  const typeLabel = (documentType: string): string => {
    const labelKey = listTypeLabelKey(documentType)
    if (labelKey === null) {
      return documentType
    }
    return t(labelKey)
  }

  const normalizedQuery = searchQuery.trim().toLowerCase()
  const documentMatchesQuery = (document: Document): boolean => {
    const haystack = `${typeLabel(document.document_type)} ${document.vehicle_name} ${document.created_at}`
    return haystack.toLowerCase().includes(normalizedQuery)
  }
  const visibleDocuments = normalizedQuery.length === 0 ? documents : documents.filter(documentMatchesQuery)

  const typeMenu = (align: 'right' | 'center'): JSX.Element => {
    const position = align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2'
    return (
      <div
        className={`w-48 p-1 absolute top-full z-20 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden ${position}`}
        style={{ boxShadow: menuShadow }}>
        <div className="self-stretch flex flex-col justify-start items-start">
          {documentTypeOptions.map((option) => (
            <button
              type="button"
              key={option.type}
              onClick={() => {
                handleSelectType(option.type)
              }}
              className="self-stretch px-2 py-1 bg-backgrounds-bg-field-component rounded-sm inline-flex justify-start items-center hover:bg-backgrounds-bg-field-component-hover">
              <div className="flex-1 text-left text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5">
                {t(option.labelKey)}
              </div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 self-stretch bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base inline-flex flex-col justify-start items-start">
      <div className="self-stretch h-14 px-4 py-3 inline-flex justify-between items-center">
        <div className="justify-start text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5">
          {t('clients.documents')}
        </div>
        <div className="self-stretch flex justify-start items-center gap-3">
          <div
            className="w-44 self-stretch px-2 bg-backgrounds-bg-field-component rounded-md flex justify-center items-center gap-2 overflow-hidden"
            style={{ boxShadow: searchShadow }}>
            <SearchSvg className="size-6 text-icon-secondary shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value)
              }}
              placeholder={t('clients.search')}
              className="flex-1 min-w-0 bg-transparent outline-none text-foregrounds-fg-base text-xs font-normal font-Montserrat leading-5 placeholder:text-foregrounds-fg-muted"
            />
          </div>
          <div className="relative" ref={headerMenuRef}>
            <SecondaryButton
              size="base"
              focused={menuAnchor === 'header'}
              onClick={() => {
                toggleMenu('header')
              }}
              icon={<PlusSvg className="w-3.5 h-3.5 text-icon-secondary" />}>
              {t('my_vehicles.add_document')}
            </SecondaryButton>
            {menuAnchor === 'header' && typeMenu('right')}
          </div>
        </div>
      </div>
      <div className="self-stretch flex-1 px-3 pb-3 flex flex-col justify-start items-start gap-2.5 overflow-visible">
        {isEmpty ? (
          <div className="self-stretch flex-1 p-6 bg-backgrounds-bg-field-component rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base flex flex-col justify-center items-center gap-6 overflow-visible">
            <div className="self-stretch flex flex-col justify-center items-center gap-3">
              <div className="size-8 flex justify-center items-center">
                <DocumentFileSvg className="size-8 text-foregrounds-fg-base" />
              </div>
              <div className="self-stretch flex flex-col justify-center items-center gap-1">
                <div className="self-stretch text-center text-foregrounds-fg-base text-xl font-semibold font-Montserrat leading-5">
                  {t('my_vehicles.documents_empty_title')}
                </div>
                <div className="self-stretch text-center text-foregrounds-fg-subtle text-sm font-medium font-Montserrat leading-5">
                  {t('clients.documents_empty_subtitle')}
                </div>
              </div>
            </div>
            <div className="relative pb-2 inline-flex justify-center items-center" ref={emptyMenuRef}>
              <SecondaryButton
                focused={menuAnchor === 'empty'}
                onClick={() => {
                  toggleMenu('empty')
                }}
                icon={<PlusSvg className="w-3.5 h-3.5 text-icon-secondary" />}>
                {t('my_vehicles.add_document')}
              </SecondaryButton>
              {menuAnchor === 'empty' && typeMenu('center')}
            </div>
          </div>
        ) : (
          <div className="self-stretch flex-1 relative bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base flex flex-col justify-start items-center overflow-visible">
            <div className="self-stretch flex-1 flex flex-col justify-start items-start overflow-visible">
              {visibleDocuments.map((document, index) => {
                const rowKey = `${document.document_type}-${document.id}`
                const motorbikeId = document.motorbike_id
                const canOpenVehicle =
                  motorbikeId !== null && typeof motorbikeId !== 'undefined' && String(motorbikeId).length > 0
                return (
                  <React.Fragment key={rowKey}>
                    {index > 0 && <div className="self-stretch h-px bg-borders-border-base" />}
                    <DocumentListRow
                      label={typeLabel(document.document_type)}
                      date={formatDocumentDate(document.created_at)}
                      vehicleName={document.vehicle_name}
                      {...(canOpenVehicle
                        ? {
                            onOpenVehicle: () => {
                              onOpenVehicle(document)
                            }
                          }
                        : {})}
                      isMenuOpen={openRowKey === rowKey}
                      onToggleMenu={() => {
                        setOpenRowKey(openRowKey === rowKey ? null : rowKey)
                      }}
                      onCloseMenu={() => {
                        setOpenRowKey(null)
                      }}
                      onOpen={() => {
                        onOpenDocument(document)
                      }}
                      onDownload={() => {
                        onDownloadDocument(document)
                      }}
                      onEdit={() => {
                        onEditDocument(document)
                      }}
                      onDelete={() => {
                        onDeleteDocument(document)
                      }}
                    />
                  </React.Fragment>
                )
              })}
            </div>
            {normalizedQuery.length === 0 && (
              <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientDocumentsPanel
